import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import Loader from "@iso/components/utility/loader.js";
import { Button, Dropdown, Menu, message, Empty, Popconfirm, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  StopOutlined,
  DownOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import ContractorPageWrapper from "./Contractor.style";
import { contractorService } from "../../../services/contractor.service";
import { registerServices } from "../../../services/contractorRegister.service";
import RegisterTab from "./RegisterTab";
import ApproveContractorModal from "./ApproveContractorModal";
import { REGISTER_CONFIGS } from "@iso/constants/contractorRegisters";
import {
  CONTRACTOR_STATUS,
  CONTRACTOR_STATUS_META,
  CONTRACTOR_RISK_META,
  CONTRACTOR_TYPES,
  ENGAGEMENT_TYPES,
  SUPERVISION_LEVELS,
  APPROVAL_AUTHORITIES,
  HAZARD_FLAGS,
  PROFILE_TABS,
  contractorAvatarColor,
  contractorInitials,
} from "@iso/constants/contractor";

const fmtDate = (v) => (v ? moment(v).format("DD MMM YYYY") : "—");
const dash = (v) => (v === undefined || v === null || v === "" ? "—" : v);
const lookup = (list, value) =>
  list.find((x) => x.value === value)?.label || dash(value);

/**
 * Contractor profile — Phase 1: header KPI strip + Overview tab.
 * The remaining tabs are declared in PROFILE_TABS with implemented:false
 * and render a "coming in a later phase" placeholder, so the tab strip
 * matches the final design from day one and each phase just flips a flag.
 */
const ContractorProfile = () => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [contractor, setContractor] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [statusSaving, setStatusSaving] = useState(false);
  // Worker list feeds the holder selector on the Licences/Inductions
  // registers; fetched once the profile loads.
  const [workers, setWorkers] = useState([]);
  const activePermission = useSelector(
    (state) => state.permission.activePermission
  );
  // Status transitions are owner/admin-gated server-side; hide the
  // buttons for everyone else rather than render an action that 403s.
  const canManage =
    activePermission == "owner" || activePermission == "admin";

  const fetchData = () => {
    setLoading(true);
    contractorService
      .getOne(id)
      .then((res) => {
        if (res?.code === "0000") {
          setContractor(res.contractor);
        } else {
          message.error(res?.message || "Contractor not found");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load contractor:", err);
        message.error("Failed to load contractor");
        setLoading(false);
      });
  };

  // Header summary only, without the page loader — lets a register write
  // update the KPI strip in place instead of blanking the tab the user
  // is working in. Failures stay silent; a background refresh shouldn't
  // toast over the success message of the save that triggered it.
  const refreshSummary = () => {
    contractorService
      .getOne(id)
      .then((res) => {
        if (res?.code === "0000") setContractor(res.contractor);
      })
      .catch((err) => console.error("Failed to refresh contractor:", err));
  };

  const fetchWorkers = () => {
    registerServices["contractor-worker"]
      .list(id)
      .then((res) => {
        if (res?.code === "0000") setWorkers(res.items || []);
      })
      .catch((err) => console.error("Failed to load workers:", err));
  };

  useEffect(() => {
    fetchData();
    fetchWorkers();
  }, [id]);

  /**
   * Every register write moves the parent's compliance counts, and a
   * Personnel write also changes the worker pool the Licences and
   * Inductions holder selectors are built from. Neither was refreshed
   * before, so the natural flow — register the workers, then attach
   * their credentials — hit an empty Holder dropdown until the user
   * reloaded the whole page.
   */
  const handleRegisterChanged = (registerKey) => {
    refreshSummary();
    if (registerKey === "personnel") fetchWorkers();
  };

  useEffect(() => {
    const el = document.querySelector(".isomorphicContent");
    if (!el) return undefined;
    const prev = el.style.background;
    el.style.background = "#ffffff";
    return () => {
      el.style.background = prev;
    };
  }, []);

  // Approving is two decisions, not one — compliance, and who may sign in. Under
  // MANUAL contractor approval the second one is a real question, so ask it
  // instead of silently answering "nobody" as this button used to. Under
  // automatic approval a check-in has already produced an active login, and under
  // reject access is refused outright, so in both cases approve straight through.
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  // Bumped to force the register tabs to re-read. RegisterTab fetches on mount
  // and when its endpoint/contractor changes, so after granting access the
  // Personnel tab's AirAgri Access column would otherwise show the old answer.
  const [registerRefresh, setRegisterRefresh] = useState(0);
  // Which worker's access is mid-change, so only that row's control is busy.
  const [accessBusyId, setAccessBusyId] = useState(null);

  /**
   * Grant or revoke ONE worker's login, from the row they are already looking at.
   *
   * Granting is the same decision the Approve Contractor modal makes in bulk, so
   * it grants immediately. Revoking asks first: it removes the person's access to
   * the property, and while their account and compliance record survive, that is
   * not obvious from a single icon.
   */
  const changeWorkerAccess = (worker, grant, refresh) => {
    setAccessBusyId(worker.id);
    return contractorService
      .workerAccess(worker.id, grant)
      .then((res) => {
        if (res?.code === "0000") {
          message.success(res.message || (grant ? "Contractor user accepted." : "Access removed."));
          return refresh();
        }
        message.error(res?.message || "Could not change this worker's access.");
        return null;
      })
      .catch(() => message.error("Could not change this worker's access."))
      .finally(() => setAccessBusyId(null));
  };

  /**
   * The AirAgri Access column says where each worker stands; this is the action
   * that changes it. Icon buttons with tooltips, to sit with Edit and Delete in
   * the same Actions cell rather than crowding the row with text.
   *
   * Deliberately contextual rather than one toggle — the three cases mean
   * different things, and one of them is impossible. Accepting here is the same
   * action and outcome as "Accept contractor user" under Property Settings → User
   * Access Control; the wording matches so nobody has to wonder whether it is.
   */
  const workerAccessAction = (row, refresh) => {
    if (!canManage) return null;
    const busy = accessBusyId === row.id;

    if (row.site_access === "approved") {
      return (
        <Popconfirm
          title="Remove this worker's access?"
          description="Their Site Access goes back to Pending and they will no longer be able to sign in to this property. Their AirAgri account and their compliance record are kept."
          okText="Remove access"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={() => changeWorkerAccess(row, false, refresh)}
        >
          <Tooltip title="Remove contractor user access">
            <Button size="small" type="text" danger loading={busy} icon={<StopOutlined />} />
          </Tooltip>
        </Popconfirm>
      );
    }

    // A deliberate Rejected holds until somebody changes it, in the Edit Worker
    // form. Offering a tick that the API would refuse is worse than saying so.
    if (row.site_access === "rejected") {
      return (
        <Tooltip title="This worker has been rejected. Change their Site Access to Pending or Approved first.">
          <Button size="small" type="text" disabled icon={<CheckCircleFilled />} />
        </Tooltip>
      );
    }

    // No email means no account can be created — say why rather than offering a
    // button that only ever fails.
    if (!row.email) {
      return (
        <Tooltip title="Add an email address for this worker before accepting them.">
          <Button size="small" type="text" disabled icon={<CheckCircleFilled />} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Accept contractor user">
        <Button
          size="small"
          type="text"
          loading={busy}
          icon={<CheckCircleFilled style={{ color: "#067647" }} />}
          onClick={() => changeWorkerAccess(row, true, refresh)}
        />
      </Tooltip>
    );
  };

  const startApproval = () => {
    setStatusSaving(true);
    contractorService
      .accessCandidates(contractor.id)
      .then((res) => {
        if (res?.code === "0000" && res.requires_approval) {
          setApproveModalOpen(true);
        } else {
          // Nothing to decide — behave exactly as before.
          changeStatus(CONTRACTOR_STATUS.APPROVED);
        }
      })
      .catch(() => changeStatus(CONTRACTOR_STATUS.APPROVED))
      .finally(() => setStatusSaving(false));
  };

  const changeStatus = (status) => {
    setStatusSaving(true);
    contractorService
      .changeStatus(contractor.id, status)
      .then((res) => {
        if (res?.code === "0000") {
          message.success("Status updated");
          setContractor(res.contractor);
        } else {
          message.error(res?.message || "Failed to update status");
        }
        setStatusSaving(false);
      })
      .catch((err) => {
        console.error("Failed to update contractor status:", err);
        message.error("Failed to update status");
        setStatusSaving(false);
      });
  };

  if (loading) {
    return (
      <LayoutWrapper>
        <ContractorPageWrapper>
          <Box className="contractor-box"><Loader /></Box>
        </ContractorPageWrapper>
      </LayoutWrapper>
    );
  }

  if (!contractor) {
    return (
      <LayoutWrapper>
        <ContractorPageWrapper>
          <Box className="contractor-box">
            <Empty description="Contractor not found" />
          </Box>
        </ContractorPageWrapper>
      </LayoutWrapper>
    );
  }

  const c = contractor;
  const statusMeta =
    CONTRACTOR_STATUS_META[c.status] ||
    CONTRACTOR_STATUS_META[CONTRACTOR_STATUS.INFORMATION_INCOMPLETE];
  const riskMeta = CONTRACTOR_RISK_META[c.risk_level];

  const statusMenu = (
    <Menu
      onClick={({ key }) => changeStatus(key)}
      items={Object.entries(CONTRACTOR_STATUS_META)
        .filter(([value]) => value !== c.status)
        .map(([value, meta]) => ({ key: value, label: meta.label }))}
    />
  );

  const renderOverview = () => (
    <div className="detail-cards">
      <div className="detail-card">
        <div className="detail-card-head">Business Details</div>
        <div className="detail-card-body">
          <div className="detail-grid">
            <div><div className="d-label">Business Name</div><div className="d-value">{dash(c.business_name)}</div></div>
            <div><div className="d-label">Trading Name</div><div className="d-value">{dash(c.trading_name)}</div></div>
            <div><div className="d-label">Contractor Type</div><div className="d-value">{lookup(CONTRACTOR_TYPES, c.contractor_type)}</div></div>
            <div><div className="d-label">Category</div><div className="d-value">{dash(c.category)}</div></div>
            <div>
              <div className="d-label">ABN</div>
              <div className="d-value">
                {dash(c.abn)}{" "}
                {c.abn_verified && <span className="verified-tag"><CheckCircleOutlined /> Verified</span>}
              </div>
            </div>
            <div><div className="d-label">GST Registered</div><div className="d-value">{c.gst_registered ? "Yes" : "No"}</div></div>
            <div><div className="d-label">Business Address</div><div className="d-value">{dash(c.business_address)}</div></div>
            <div><div className="d-label">Service Area</div><div className="d-value">{dash(c.service_area)}</div></div>
            <div><div className="d-label">Website</div><div className="d-value">{dash(c.website)}</div></div>
          </div>
        </div>
      </div>

      <div className="detail-card">
        <div className="detail-card-head">Contact Details</div>
        <div className="detail-card-body">
          <div className="detail-grid">
            <div><div className="d-label">Primary Contact</div><div className="d-value">{dash(c.primary_contact_name)}</div></div>
            <div><div className="d-label">Role</div><div className="d-value">{dash(c.primary_contact_role)}</div></div>
            <div><div className="d-label">Phone</div><div className="d-value">{dash(c.primary_contact_phone)}</div></div>
            <div><div className="d-label">Email</div><div className="d-value">{dash(c.primary_contact_email)}</div></div>
            <div><div className="d-label">After-Hours</div><div className="d-value">{dash(c.after_hours_phone)}</div></div>
            <div><div className="d-label">Emergency Contact</div><div className="d-value">{dash(c.emergency_contact_name)} {c.emergency_contact_phone ? `· ${c.emergency_contact_phone}` : ""}</div></div>
          </div>
        </div>
      </div>

      <div className="detail-card">
        <div className="detail-card-head">Engagement Details</div>
        <div className="detail-card-body">
          <div className="detail-grid">
            <div><div className="d-label">Engagement Type</div><div className="d-value">{lookup(ENGAGEMENT_TYPES, c.engagement_type)}</div></div>
            <div><div className="d-label">Commencement</div><div className="d-value">{fmtDate(c.contract_commencement)}</div></div>
            <div><div className="d-label">Expiry</div><div className="d-value">{fmtDate(c.contract_expiry)}</div></div>
            <div><div className="d-label">Next Review Date</div><div className="d-value">{fmtDate(c.next_review_date)}</div></div>
            <div><div className="d-label">Date First Engaged</div><div className="d-value">{fmtDate(c.date_first_engaged)}</div></div>
            <div><div className="d-label">Internal Owner</div><div className="d-value">{dash(c.internal_owner_name)}</div></div>
            <div><div className="d-label">Subcontracting</div><div className="d-value">{c.subcontracting_approved ? "Approved" : "Not approved"}</div></div>
            <div><div className="d-label">Approved Locations</div><div className="d-value">{(c.approved_property_names || []).join(", ") || "—"}</div></div>
            <div>
              <div className="d-label">Contract Document</div>
              <div className="d-value">
                {c.contract_attachment?.full_path ? (
                  <a href={c.contract_attachment.full_path} target="_blank" rel="noopener noreferrer">
                    {c.contract_attachment.display_name || "Download contract"}
                  </a>
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}><div className="d-label">Scope of Services</div><div className="d-value">{dash(c.scope_description)}</div></div>
          </div>
        </div>
      </div>

      <div className="detail-card">
        <div className="detail-card-head">Risk Classification</div>
        <div className="detail-card-body">
          <div className="detail-grid">
            <div><div className="d-label">Risk Level</div><div className="d-value">{riskMeta ? <span className={`risk-badge ${riskMeta.className}`}>{riskMeta.label}</span> : "—"}</div></div>
            <div><div className="d-label">Supervision</div><div className="d-value">{lookup(SUPERVISION_LEVELS, c.supervision_level)}</div></div>
            <div><div className="d-label">Approval Authority</div><div className="d-value">{lookup(APPROVAL_AUTHORITIES, c.approval_authority)}</div></div>
            <div style={{ gridColumn: "1 / -1" }}>
              <div className="d-label">Hazards</div>
              <div className="d-value">
                {(c.hazard_flags || [])
                  .map((k) => HAZARD_FLAGS.find((h) => h.key === k)?.label || k)
                  .join(", ") || "—"}
              </div>
            </div>
          </div>
          {c.internal_notes && (
            <div className="restricted-note" style={{ marginTop: 14 }}>
              <strong>Internal Notes (Restricted):</strong> {c.internal_notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <LayoutWrapper>
      <ContractorPageWrapper>
        <Box className="contractor-box">
          <div className="ctr-breadcrumb">
            <span
              style={{ cursor: "pointer" }}
              onClick={() => history.push("/contractors")}
            >
              Contractor Management
            </span>
            <span className="crumb-sep">›</span>
            <span className="crumb-current">{c.business_name}</span>
          </div>

          <div className="profile-header-card">
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div className="profile-topline">
                <Button
                  shape="circle"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => history.push("/contractors")}
                />
                <div
                  className="ctr-avatar"
                  style={{ background: contractorAvatarColor(c.business_name), width: 48, height: 48, fontSize: 16 }}
                >
                  {contractorInitials(c.business_name)}
                </div>
                <div>
                  <span className="profile-name">{c.business_name}</span>{" "}
                  {c.trading_name && (
                    <span className="profile-trading">(t/a {c.trading_name})</span>
                  )}{" "}
                  <span className={`status-pill ${statusMeta.className}`}>
                    <span className="status-dot" />
                    {statusMeta.label}
                  </span>
                  <div className="profile-meta">
                    {c.category && <span>{c.category}</span>}
                    <span>{lookup(CONTRACTOR_TYPES, c.contractor_type)}</span>
                    {c.abn && <span>ABN {c.abn}</span>}
                    {c.abn_verified && (
                      <span className="verified-tag"><CheckCircleOutlined /> Verified</span>
                    )}
                  </div>
                </div>
              </div>
              {canManage && (
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  {/* Outlined partner to .btn-dark-green — a plain antd Button
                      is 32px and sat short beside the 40px primary. */}
                  <Button
                    className="btn-outline-green"
                    onClick={() => history.push(`/contractors/${c.id}/edit`)}
                  >
                    Edit Profile
                  </Button>
                  {c.status !== CONTRACTOR_STATUS.APPROVED ? (
                    <Button
                      type="primary"
                      className="btn-dark-green"
                      loading={statusSaving}
                      onClick={startApproval}
                    >
                      Approve Contractor
                    </Button>
                  ) : (
                    <Dropdown overlay={statusMenu} trigger={["click"]}>
                      {/* Same treatment: this replaces the primary in the
                          approved state, so the pair must still line up. */}
                      <Button className="btn-outline-green" loading={statusSaving}>
                        Change Status <DownOutlined />
                      </Button>
                    </Dropdown>
                  )}
                </div>
              )}
            </div>

            {c.is_expiring && (
              <div className="warn-banner">
                <ClockCircleOutlined /> Contract expires{" "}
                {moment(c.contract_expiry).fromNow()} (
                {fmtDate(c.contract_expiry)}) — renewal required before next
                engagement.
              </div>
            )}

            <div className="profile-kpis">
              <div className="kpi">
                <div className="kpi-label">Compliance Score</div>
                <div className="kpi-value">{c.compliance_score != null ? `${c.compliance_score}%` : "—"}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Risk Level</div>
                <div className="kpi-value">
                  {riskMeta ? <span className={`risk-badge ${riskMeta.className}`}>{riskMeta.label}</span> : "—"}
                </div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Approved Workers</div>
                <div className="kpi-value">{c.workers_total > 0 ? `${c.workers_approved} of ${c.workers_total}` : "—"}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Approved Assets</div>
                <div className="kpi-value">{c.assets_total > 0 ? `${c.assets_approved} of ${c.assets_total}` : "—"}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Properties</div>
                <div className="kpi-value">{(c.approved_property_ids || []).length || "—"}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Expiring Docs</div>
                <div className="kpi-value">{c.expiring_docs ?? 0}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Open Actions</div>
                <div className="kpi-value">0</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Next Review</div>
                <div className="kpi-value" style={{ fontSize: 13 }}>{fmtDate(c.next_review_date)}</div>
              </div>
            </div>
          </div>

          <div className="profile-tabs">
            {PROFILE_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`ctr-tab ${activeTab === t.key ? "active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {(() => {
            const tab = PROFILE_TABS.find((t) => t.key === activeTab);
            if (activeTab === "overview") return renderOverview();
            if (tab?.register) {
              return (
                <RegisterTab
                  key={`${tab.register}-${registerRefresh}`}
                  config={REGISTER_CONFIGS[tab.register]}
                  contractorId={c.id}
                  // The contractor's property scopes which induction forms can
                  // be run for its workers.
                  propertyId={c.property_id}
                  canManage={canManage}
                  workerOptions={workers}
                  rowActions={tab.register === "personnel" ? workerAccessAction : undefined}
                  onChanged={() => handleRegisterChanged(tab.register)}
                />
              );
            }
            // Legal & Contracts: reuse the Overview engagement/contract
            // data rather than a register — no child table backs it.
            return (
              <div className="detail-cards">
                <div className="detail-card">
                  <div className="detail-card-head">Contract &amp; Scope</div>
                  <div className="detail-card-body">
                    <div className="detail-grid">
                      <div><div className="d-label">Engagement Type</div><div className="d-value">{lookup(ENGAGEMENT_TYPES, c.engagement_type)}</div></div>
                      <div><div className="d-label">Commencement</div><div className="d-value">{fmtDate(c.contract_commencement)}</div></div>
                      <div><div className="d-label">Expiry</div><div className="d-value">{fmtDate(c.contract_expiry)}</div></div>
                      <div><div className="d-label">Subcontracting</div><div className="d-value">{c.subcontracting_approved ? "Approved" : "Not approved"}</div></div>
                      <div style={{ gridColumn: "1 / -1" }}><div className="d-label">Approved Locations</div><div className="d-value">{(c.approved_property_names || []).join(", ") || "—"}</div></div>
                      <div style={{ gridColumn: "1 / -1" }}><div className="d-label">Scope of Services</div><div className="d-value">{dash(c.scope_description)}</div></div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div className="d-label">Contract Document</div>
                        <div className="d-value">
                          {c.contract_attachment?.full_path ? (
                            <a href={c.contract_attachment.full_path} target="_blank" rel="noopener noreferrer">
                              {c.contract_attachment.display_name || "Download contract"}
                            </a>
                          ) : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </Box>

        <ApproveContractorModal
          open={approveModalOpen}
          contractor={contractor}
          onCancel={() => setApproveModalOpen(false)}
          onApproved={(updated) => {
            setApproveModalOpen(false);
            if (updated) setContractor(updated);
            // The Personnel tab's Login column is now stale for anyone granted
            // access, so make it re-read rather than showing yesterday's answer.
            setRegisterRefresh((n) => n + 1);
          }}
        />
      </ContractorPageWrapper>
    </LayoutWrapper>
  );
};

export default ContractorProfile;
